import Box from '@sendsprint/ui-react/dist/components/Box';
import Button from '@sendsprint/ui-react/dist/components/Button';
import ExtraInfo from '@sendsprint/ui-react/dist/components/ExtraInfo';
import Icon from '@sendsprint/ui-react/dist/components/Icon';
import Text from '@sendsprint/ui-react/dist/components/Text';
import CheckmarkOutline from '@sendsprint/ui-react/dist/icons/CheckmarkOutline';
import { ErrorMessage, useField } from 'formik';
import React, { forwardRef, ReactNode, useId, useImperativeHandle, useRef } from 'react';
import FaTimes from '@src/assets/svgs/transformed/react-icons/fa/FaTimes';
import BsUpload from '@src/assets/svgs/transformed/react-icons/bs/BsUpload';
import BsCloudArrowUp from '@src/assets/svgs/transformed/react-icons/bs/BsCloudArrowUp';
import { ReactComponent as DocumentDownloadIcon } from '@src/assets/svgs/document-download.svg';
import { extractFilenameFromUrl } from '@src/utils';

interface FormFieldFileProps {
  name: string;
  extraInfo?: ReactNode;
  accept?: string;
  inputText?: string;
  otherChangeFunc?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showCustomBtn?: boolean;
  customBtnLabel?: string;
  shouldDownloadValue?: boolean;
}

export interface FormFieldFileRef {
  clickLabel: () => void;
}

const FormFieldFile = forwardRef(
  (
    {
      name,
      extraInfo,
      accept,
      otherChangeFunc,
      inputText = 'Supported file types: JPEG, PNG, PDF. Max file size: 2mb',
      showCustomBtn = false,
      customBtnLabel = 'Browse file or drag and drop photo',
      shouldDownloadValue
    }: FormFieldFileProps,
    ref: React.ForwardedRef<FormFieldFileRef>
  ) => {
    const id = useId();
    const labelRef = useRef<HTMLLabelElement>(null);
    const [{ value, onChange }, , { setValue }] = useField<File | string | null>(name);

    const fileName = typeof value === 'string' ? extractFilenameFromUrl(value) : value?.name;

    useImperativeHandle(
      ref,
      () => {
        return {
          clickLabel: () => {
            labelRef.current?.click();
          }
        };
      },
      []
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length) {
        onChange(e);
        setValue(e.target.files[0]);

        if (otherChangeFunc) {
          otherChangeFunc(e);
        }
      }
    };

    const handleBtnClick = () => {
      if (labelRef && labelRef.current) {
        labelRef.current.click();
      }
    };

    const handleRemove = () => {
      setValue(null);
    };

    const handleDeleteImage = async () => {
      if (typeof value !== 'string') return;

      window.open(value, '_blank');

      // const res = await fetch(value);
      // const blob = await res.blob();
      // const blobUrl = URL.createObjectURL(blob);

      // const link = document.createElement('a');
      // link.href = blobUrl;
      // link.download = fileName || '';
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);

      // URL.revokeObjectURL(blobUrl);
    };

    return (
      <div>
        {!value && (
          <label ref={labelRef} className="ss-relative" htmlFor={id}>
            {!showCustomBtn && (
              <button
                onClick={handleBtnClick}
                type="button"
                className="ss-w-full focus:ss-focus-ring ss-relative ss-gap-3 ss-py-10 ss-bg-neutral-100 ss-px-4 ss-flex ss-justify-center ss-items-center ss-border ss-overflow-hidden ss-border-dashed ss-border-neutral-300 ss-rounded-lg">
                <BsCloudArrowUp width={30} height={30} />
                <Box className="ss-space-y-1">
                  <Text variant="paragraphSmall" className="ss-text-neutral-400">
                    Drag and drop document here or{' '}
                    <span className="ss-text-neutral-700">Browse</span>
                  </Text>
                  <Text variant="paragraphVerySmall" className="ss-text-neutral-400">
                    {inputText}
                  </Text>
                </Box>
              </button>
            )}
            {showCustomBtn && (
              <Button onClick={handleBtnClick} isBlock label={customBtnLabel} icon={BsUpload} />
            )}
            <input
              accept={accept}
              type="file"
              name={name}
              className="ss-absolute ss-opacity-0 ss-cursor-pointer ss-top-0 ss-left-0 ss-right-0 ss-bottom-0"
              id={id}
              onChange={handleChange}
            />
          </label>
        )}
        {value && (
          <div className="ss-relative ss-py-10 ss-bg-neutral-100 ss-px-4 ss-flex ss-gap-4 ss-justify-between ss-items-center ss-border ss-overflow-hidden ss-border-dashed ss-border-neutral-300 ss-rounded-lg">
            <div className="ss-flex ss-flex-1 ss-items-center ss-gap-3">
              <Box className="ss-bg-primary1-500 ss-w-7 ss-h-7 ss-flex ss-items-center ss-justify-center ss-rounded-full">
                <Icon className="ss-text-white" svg={CheckmarkOutline} />
              </Box>
              <Text as="p" className="ss-break-all ss-flex-1">
                {fileName}
              </Text>
            </div>
            {shouldDownloadValue && typeof value === 'string' && (
              <button
                type="button"
                className="ss-w-6 ss-h-6 ss-rounded-full ss-flex ss-justify-center ss-items-center focus:ss-focus-ring"
                onClick={handleDeleteImage}>
                <DocumentDownloadIcon width={20} height={20} />
              </button>
            )}
            <button
              type="button"
              className="ss-w-6 ss-h-6 ss-rounded-full ss-flex ss-justify-center ss-items-center focus:ss-focus-ring"
              onClick={handleRemove}>
              <FaTimes width={20} height={20} />
            </button>
          </div>
        )}
        <ErrorMessage name={name}>
          {(errMessage) => <ExtraInfo variant="error" extraInfo={errMessage} />}
        </ErrorMessage>
        {extraInfo && <ExtraInfo extraInfo={extraInfo} />}
      </div>
    );
  }
);

export default FormFieldFile;
